/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { ColumnAlignment, LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LinkProps } from "@waytek/MobiusOverrides/Link/Link";
import { colorStrings, mediaQueriesBP } from "@waytek/Utilities/variables";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    image = "image",
    description = "description",
    linkLabel = "linkLabel",
    link = "link",
    useExternal = "useExternal",
}

interface Props extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.image]: string;
        [fields.description]: string;
        [fields.linkLabel]: string;
        [fields.link]: LinkFieldValue;
        [fields.useExternal]: boolean;
    };
}

export interface CatalogueStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    leftGridItem?: GridItemProps;
    imageContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: ImgProps;
    rightGridItem?: GridItemProps;
    infoContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    title?: TypographyProps;
    description?: TypographyProps;
    link?: LinkProps;
}

export const catalogueStyles: CatalogueStyles = {
    wrapper: {
        css: css`
            background: ${getColor(colorStrings.commonGlobalWhite)};
            border: 1px solid ${getColor(colorStrings.commonGlobalIndustrialTaupe)};
            height: 100%;

            ${mediaQueriesBP.maxMD} {
                padding: 15px 20px 21px;
            }

            ${mediaQueriesBP.minMD} {
                padding: 21px 21px 34px;
            }
        `,
    },
    container: {
        gap: 0.1,
    },
    leftGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            ${mediaQueriesBP.minMD} {
                padding-right: 13px;
            }
        `,
    },
    imageContainer: { gap: 0.1 },
    imageGridItem: { width: 12 },
    image: {
        css: css`
            width: 100%;
            height: auto;

            ${mediaQueriesBP.maxMD} {
                max-width: 80%;
                margin: 0 auto 15px;
            }
        `,
    },
    rightGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            ${mediaQueriesBP.minMD} {
                padding-left: 13px;
            }
        `,
    },
    infoContainer: { gap: 0.1 },
    infoGridItem: { width: 12 },
    title: {
        variant: "h3",
        transform: "none",
        mergeCss: true,
        css: css`
            margin-bottom: 9px;

            ${mediaQueriesBP.maxMD} {
                font-size: 22px;
                line-height: 26px;
                margin-bottom: 63px;
            }
        `,
    },
    description: {
        mergeCss: true,
        size: "14px",
        lineHeight: "24px",
        css: css`
            margin-bottom: 11px;
        `,
    },
    link: {
        icon: {
            position: "right",
            iconProps: {
                src: ChevronRight,
                size: 15,
            },
        },
    },
};

export const Catalogue: React.FunctionComponent<Props> = ({
    id,
    fields: { title, image, description, linkLabel, link, useExternal },
}: Props) => {
    const styles = catalogueStyles;
    const { title: linkTitle, url } = useGetLink(link);

    return (
        <StyledWrapper {...styles.wrapper} className="catalogue__widget">
            <GridContainer {...styles.container}>
                <GridItem {...styles.leftGridItem}>
                    <Img src={image} loading="lazy" altText={title} {...styles.image} />
                </GridItem>
                <GridItem {...styles.rightGridItem}>
                    <GridContainer {...styles.infoContainer}>
                        <GridItem {...styles.infoGridItem}>
                            <Typography {...styles.title}>{title}</Typography>
                        </GridItem>
                        <GridItem {...styles.infoGridItem}>
                            <Typography {...styles.description}>{description}</Typography>
                        </GridItem>
                        <GridItem {...styles.infoGridItem}>
                            <Link {...styles.link} href={url} target={useExternal ? "_blank" : ""}>
                                {linkLabel || linkTitle || url}
                            </Link>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: Catalogue,
    definition: {
        group: "Basic",
        icon: "images",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.description,
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.linkLabel,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.link,
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Page",
                    value: "",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.useExternal,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
